import React, { useState, useMemo } from 'react';
import { X, Coins } from 'lucide-react';
import { TokenPurchasePackage } from '../../types/subscription';

const PRICE_PER_MILLION = 10; // $10 USD par million de tokens
const MIN_TOKENS = 1_000_000;   // 1M
const MAX_TOKENS = 50_000_000;  // 50M
const STEP = 1_000_000;         // Pas de 1M

function formatMillions(n: number): string {
  const m = n / 1_000_000;
  return m % 1 === 0 ? `${m}M` : `${m.toFixed(1)}M`;
}

interface PremiumTokenPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseInitiated: (pkg: TokenPurchasePackage) => void;
  customerType?: string;
}

export default function PremiumTokenPurchaseModal({
  isOpen,
  onClose,
  onPurchaseInitiated,
}: PremiumTokenPurchaseModalProps) {
  const [tokens, setTokens] = useState(MIN_TOKENS);

  const priceUSD = useMemo(() => (tokens / 1_000_000) * PRICE_PER_MILLION, [tokens]);
  const percentage = ((tokens - MIN_TOKENS) / (MAX_TOKENS - MIN_TOKENS)) * 100;

  const handleConfirm = () => {
    onPurchaseInitiated({
      id: `tokens-custom-${tokens}`,
      name: `${formatMillions(tokens)} tokens`,
      tokens,
      priceUSD,
      bonusTokens: 0,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
              <Coins className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Acheter des tokens</h2>
              <p className="text-xs text-gray-500">1 message = 1 token · {PRICE_PER_MILLION}$ / million</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-6">
          {/* Selected amount display */}
          <div className="text-center">
            <div className="text-4xl font-bold text-gray-900">{formatMillions(tokens)}</div>
            <div className="text-sm text-gray-500 mt-1">tokens</div>
          </div>

          {/* Slider */}
          <div className="px-1">
            <input
              type="range"
              min={MIN_TOKENS}
              max={MAX_TOKENS}
              step={STEP}
              value={tokens}
              onChange={(e) => setTokens(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-amber-500
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-500 [&::-webkit-slider-thumb]:shadow-md
                [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white
                [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:bg-amber-500 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white"
              style={{
                background: `linear-gradient(to right, #f59e0b 0%, #f59e0b ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`,
              }}
            />
            <div className="flex justify-between mt-1.5 text-[11px] text-gray-400">
              <span>{formatMillions(MIN_TOKENS)}</span>
              <span>{formatMillions(MAX_TOKENS)}</span>
            </div>
          </div>

          {/* Summary card */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Tokens</span>
              <span className="font-medium text-gray-900">{tokens.toLocaleString('fr-FR')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Tarif</span>
              <span className="text-gray-600">{PRICE_PER_MILLION}$ / million</span>
            </div>
            <div className="border-t border-gray-200 pt-2 flex justify-between items-baseline">
              <span className="font-semibold text-gray-900">Total</span>
              <span className="text-2xl font-bold text-amber-600">${priceUSD}</span>
            </div>
          </div>

          <p className="text-[11px] text-gray-400 text-center">
            Tokens sans expiration · Utilisables pour toutes les fonctionnalités
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-lg transition-all shadow-sm"
          >
            Payer ${priceUSD}
          </button>
        </div>
      </div>
    </div>
  );
}
